import { formatDate } from '@strata/helper-functions';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { CurrentBid } from '@strata/react-current-bid';
import { DialogWithForm } from '@strata/react-dialog';
import { DollarAmount } from '@strata/react-dollar-amount';
import { FormSectionRow } from '@strata/react-form-section';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import { Worklist, type WorklistTask, WorklistTimer } from '@strata/react-worklist';
import { validationStrings } from '@strata/strata-ui-strings';
import Decimal from 'decimal.js';
import { Formik } from 'formik';
import { useState } from 'react';
import { type Location, useLocation, useNavigationType } from 'react-router';
import * as Yup from 'yup';

import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { showDialogForWorklistTypes, useReturnToWorklistCheck } from 'services/worklistHelpers';
import { useAppDispatch } from 'store';
import { type ReferralDetails } from 'store/api/treatmentApi';
import { usePostRebillAttestationMutation } from 'store/api/worklistApi';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

interface RebillingDialogProps {
    referralDetails?: ReferralDetails;
}

export const RebillingDialog = ({ referralDetails = undefined }: RebillingDialogProps) => {
    const dispatch = useAppDispatch();
    const location = useLocation() as Location<{ worklistTask?: WorklistTask }>;
    const navigationType = useNavigationType();
    const writeWorklist = usePermissionCheck(Permissions.WRITE_WORKLIST);
    const returnToWorklistCheck = useReturnToWorklistCheck();

    const [postRebillAttestation] = usePostRebillAttestationMutation();

    const worklistTask = location.state?.worklistTask;
    const [isOpen, setIsOpen] = useState(
        showDialogForWorklistTypes({
            types: [Worklist.WorkTaskType.REBILL],
            navigationType,
            location,
        }),
    );

    return (
        <Formik
            initialValues={{ attest: [] }}
            validationSchema={Yup.object({
                attest: Yup.array().min(1, validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (_values, { resetForm }) => {
                if (worklistTask) {
                    await postRebillAttestation({ taskId: worklistTask.task_id }).unwrap();

                    // Update worklist tasks in store
                    await dispatch(fetchWorklistTasks());

                    SuccessToast({ message: 'Attestation completed successfully.' });

                    setIsOpen(false);
                    resetForm();
                    returnToWorklistCheck();
                }
            }}
        >
            {
                // eslint-disable-next-line complexity
                (formik) => (
                    <DialogWithForm
                        dialogTitle={worklistTask?.task_type_display_name ?? 'Rebilling'}
                        width={800}
                        isOpen={isOpen}
                        primaryButtonLabel="Save"
                        isPrimaryButtonDisabled={
                            !writeWorklist ||
                            !(formik.isValid && formik.dirty) ||
                            formik.isSubmitting
                        }
                        secondaryButtonLabel="Cancel"
                        onSecondaryButtonClick={() => {
                            setIsOpen(false);
                            formik.handleReset();
                        }}
                        dialogContent={
                            <>
                                <div className="margin__bottom--large">
                                    This referral has been billed at an incorrect price. Please
                                    revise the claim with the expected ingredient cost below. This
                                    cost is the Claim AWP (Unit AWP x Quantity) minus the Servicing
                                    Rate.
                                </div>
                                <div className="sds_rebilling-dialog__section-header">
                                    Referral Overview
                                </div>
                                <FormSectionRow
                                    rowContent={
                                        <>
                                            {worklistTask && (
                                                <WorklistTimer
                                                    dueDate={worklistTask.due_date}
                                                    warningDate={worklistTask.warning_date}
                                                    completedDate={worklistTask.completed_date}
                                                />
                                            )}
                                            <DetailLabelValuePair
                                                label="Patient"
                                                value={worklistTask?.task_info?.patient_name}
                                            />
                                            <DetailLabelValuePair
                                                label="DOB"
                                                value={formatDate(referralDetails?.memberDOB)}
                                            />
                                            <DetailLabelValuePair
                                                label="Member ID"
                                                value={referralDetails?.memberId}
                                            />
                                            <DetailLabelValuePair
                                                label="Rx Number"
                                                value={worklistTask?.task_info?.rx_number}
                                            />
                                        </>
                                    }
                                />
                                <div className="sds_rebilling-dialog__section-header">
                                    Submitted Price
                                </div>
                                <FormSectionRow
                                    rowContent={
                                        <>
                                            <DetailLabelValuePair
                                                label="Date Of Service"
                                                value={formatDate(
                                                    worklistTask?.task_info?.date_of_service,
                                                )}
                                            />
                                            <DetailLabelValuePair
                                                label="Adjudication Date"
                                                value={formatDate(
                                                    worklistTask?.task_info?.adjudication_date,
                                                )}
                                            />
                                            <DetailLabelValuePair
                                                label="Submitted Price"
                                                value={
                                                    <DollarAmount
                                                        className="sds_rebilling-dialog__price--incorrect"
                                                        amount={
                                                            worklistTask?.task_info?.incorrect_price
                                                        }
                                                        showZeroValue
                                                        large
                                                    />
                                                }
                                            />
                                            <DetailLabelValuePair
                                                label="Quantity"
                                                value={worklistTask?.task_info?.quantity}
                                            />
                                            <DetailLabelValuePair
                                                label="Claim Number"
                                                value={worklistTask?.task_info?.claim_id}
                                            />
                                            <DetailLabelValuePair
                                                label="Billing NPI"
                                                value={
                                                    worklistTask?.task_info?.billing_supplier_npi
                                                }
                                            />
                                        </>
                                    }
                                />
                                <div className="sds_rebilling-dialog__section-header">
                                    Rebilling Price
                                </div>
                                <FormSectionRow
                                    rowContent={
                                        <>
                                            <DetailLabelValuePair
                                                label="Claim AWP (Unit AWP x Quantity)"
                                                value={
                                                    <DollarAmount
                                                        amount={
                                                            worklistTask?.task_info?.unit_price &&
                                                            worklistTask?.task_info?.quantity &&
                                                            Decimal.mul(
                                                                worklistTask.task_info.unit_price,
                                                                worklistTask.task_info.quantity,
                                                            ).toNumber()
                                                        }
                                                        showZeroValue
                                                        large
                                                    />
                                                }
                                            />
                                            <DetailLabelValuePair
                                                label="Servicing Rate"
                                                value={
                                                    <CurrentBid
                                                        bidAmount={referralDetails?.winningBid}
                                                        referentPriceType={
                                                            referralDetails?.referentPriceType
                                                        }
                                                    />
                                                }
                                            />
                                            <DetailLabelValuePair
                                                label="Ingredient Cost (Claim AWP - Servicing Rate)"
                                                value={
                                                    <DollarAmount
                                                        className="sds_rebilling-dialog__price--correct"
                                                        amount={
                                                            worklistTask?.task_info?.expected_price
                                                        }
                                                        showZeroValue
                                                        large
                                                    />
                                                }
                                            />
                                        </>
                                    }
                                />
                                <div className="sds_rebilling-dialog__section-header margin__bottom--small">
                                    Confirmation
                                </div>
                                <FormSectionRow
                                    rowContent={
                                        <CheckboxListFormik
                                            name="attest"
                                            required
                                            disabled={!writeWorklist}
                                            options={[
                                                {
                                                    value: 'attest',
                                                    component: (
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <span>
                                                                I have rebilled this claim at the
                                                                correct ingredient cost of&nbsp;
                                                            </span>
                                                            <DollarAmount
                                                                amount={
                                                                    worklistTask?.task_info
                                                                        ?.expected_price
                                                                }
                                                                showZeroValue
                                                                large
                                                            />
                                                            <span>.</span>
                                                        </span>
                                                    ),
                                                },
                                            ]}
                                        />
                                    }
                                />
                            </>
                        }
                    />
                )
            }
        </Formik>
    );
};
